import headerTemplate from "../templates/header-leaf";
import {
  appendCountryLanguage,
  updateClassNames,
  hideElementInIframe,
  addHoverForNav,
  removeAllHoverForNav,
  toggleHoverForNav,
  listenForImportantMessage,
  trackEvent,
  ACTION_KEY_CODES,
  loadScript,
  injectURWFont,
  getCountry,
  getLanguage,
  buildHeadMetaItems,
  disableAdobeLaunch,
  toggleRegularPage,
  setCountryAndLanguageCookies,
  getOneTrustLang,
} from "./GeneralHelpers";
import ArcWebSettings from "./Arcteryx";
import { setNavigationLinkClickedAnalyticsListeners } from "./Analytics";
import { loadBotDefence } from "../helpers/botDefence";

class Header extends HTMLElement {
  constructor() {
    super();
    this.root = this.attachShadow({ mode: "open" });
    loadBotDefence();
  }

  connectedCallback() {
    this.country = getCountry(this);
    this.language = getLanguage(this);
    this.market = "LEAF";
    this.season = this.hasAttribute("season") ? this.getAttribute("season") : "S";
    this.year = this.hasAttribute("year") ? this.getAttribute("year") : new Date().getFullYear().toString();
    this.searchLoaded = false;
    this.fdp = this.hasAttribute("fdp") && this.getAttribute("fdp") === "true";
    this.disableSearchCss = this.hasAttribute("disableSearchCss") && this.getAttribute("disableSearchCss") === "true";

    // set cookies for country and language
    setCountryAndLanguageCookies(this.country, this.language);

    // SWAT-8244 add language in html tag
    document.getElementsByTagName("html")[0].lang = getOneTrustLang(this.language);

    // Add header content to body
    const header = headerTemplate.content.cloneNode(true);
    this.root.appendChild(header);

    // Load Adobe Launch ADOBE_LAUNCH_URL_LEAF is set in webpack.common.js
    if (!this.fdp && ADOBE_LAUNCH_URL_LEAF && !disableAdobeLaunch()) {
      loadScript(ADOBE_LAUNCH_URL_LEAF);
    }

    if (!this.fdp && (typeof ARCTERYX === "undefined" || typeof ARCTERYX.analytics === "undefined")) {
      loadScript(`${SCRIPTS_URL}/js/analytics.min.js`);
    }

    if (!this.fdp) {
      ArcWebSettings.init("LEAF", this.season, this.year, this.language, this.country);
    }

    buildHeadMetaItems();
    this.bindListeners(this.root);
    appendCountryLanguage(this.root, this.country, this.language);
    updateClassNames(this.root, ".LEAF", this.country, this.language);

    if (!this.fdp) {
      // FDP pages get this font loaded through js-helpers <GlobalStyles />
      injectURWFont();
    }
    hideElementInIframe(this.root, ".LEAF");
    this.style.setProperty("min-height", "50px", "important");
  }

  createSearchContainer() {
    const searchContainerName = "DeadBirdSearchContainer";
    let searchContainer = document.getElementById(searchContainerName);
    if (!searchContainer) {
      searchContainer = document.createElement("div");
      searchContainer.id = searchContainerName;
      this.insertAdjacentElement("afterend", searchContainer);
    } else {
      searchContainer = document.getElementById(searchContainerName);
    }
    return searchContainer;
  }

  async loadSearch(el) {
    this.search = await import(/* webpackChunkName: "search-outdoor" */ "../../search/index.js");
    const searchIconEl = el.querySelector(".header-search");
    searchIconEl.style.opacity = 1;
    searchIconEl.addEventListener(
      "click",
      () => {
        this.openSearch(el);
      },
      true
    );
    document.addEventListener("search-closed", () => {
      toggleRegularPage(true);
    });
    if (window.location.search.match(/search=/)) {
      return this.openSearch(el);
    }
    searchIconEl.click();
  }

  openSearch(el) {
    toggleRegularPage(false);
    const searchContainer = this.createSearchContainer();
    const searchIconEl = el.querySelector(".header-search");
    searchContainer.classList.toggle("open");
    const indexConstructor = {
      market: this.market,
      language: this.language,
      country: this.country,
    };
    this.search.InitSearch(el.searchIsOpen, searchContainer, indexConstructor);
    searchIconEl.style.removeProperty("opacity");
    document.body.classList.toggle("search-mode");
    el.firstElementChild.classList.toggle("search-mode");
  }

  bindListeners(el) {
    if (window.location.search.match(/search=/) && !document.querySelector("arcteryx-search")) {
      this.loadSearch(el);
    }

    el.querySelector(".header-search").addEventListener(
      "click",
      () => {
        this.loadSearch(el);
      },
      { once: true }
    );

    document.addEventListener("search-closed", () => {
      el.querySelector(".header-search").style.opacity = 1;
      document.body.classList.toggle("search-mode");
      el.firstElementChild.classList.toggle("search-mode");
      document.getElementById("DeadBirdSearchContainer").classList.toggle("open");
    });
    window.onload = function() {
      el.querySelector(".header-search").style.opacity = "1";
    };

    setNavigationLinkClickedAnalyticsListeners(el, "nav .sub-menu .container ul li a");
    el.querySelectorAll("nav .sub-menu .container ul li a").forEach(navLink => {
      if (window.innerWidth >= 930) {
        // Show feature image on link hover
        const featuredEl = this.getClosest(navLink, ".container").querySelector(".feature .content");
        navLink.addEventListener(
          "mouseover",
          () => {
            this.getAndSetFeatureImage(navLink);
            featuredEl.classList.add("hovered");
          },
          { passive: true }
        );
        navLink.addEventListener(
          "mouseleave",
          () => {
            featuredEl.classList.remove("hovered");
          },
          { passive: true }
        );
      }
    });

    // Track which primary nav links are clicked
    el.querySelectorAll(".head").forEach(topLevelMenuItem => {
      topLevelMenuItem.addEventListener(
        "click",
        function() {
          if (window.innerWidth > 768) {
            trackEvent({
              category: "TopNavMenu",
              action: "MenuSelected",
              label: this.id,
              eVars: { eVar40: this.id },
              events: "event40",
            });
          }
        },
        { passive: true }
      );
    });
    el.querySelectorAll("nav .sub-menu").forEach(dropDown => {
      dropDown.addEventListener(
        "mouseover",
        () => {
          addHoverForNav(dropDown, el, "LEAF");
        },
        { passive: true }
      );
      dropDown.addEventListener(
        "mouseleave",
        () => {
          removeAllHoverForNav(el, "LEAF");
          if (dropDown.querySelectorAll(".mobilefly li").length) {
            const defaultFeature = dropDown.querySelectorAll(".mobilefly li")[0].querySelector("a");
            setTimeout(() => {
              this.getAndSetFeatureImage(defaultFeature);
            }, 50);
          }
        },
        { passive: true }
      );
    });

    el.querySelectorAll(".is--tabbable-hidden").forEach(tabLink => {
      tabLink.setAttribute("tabindex", "-1");
    });

    if (window.innerWidth < 769) {
      el.querySelector("nav #products.head").addEventListener(
        "click",
        e => {
          toggleHoverForNav(e.target, el, "LEAF");
        },
        { passive: true }
      );
    } else {
      el.querySelectorAll("nav #products.head").forEach(dropDown => {
        dropDown.addEventListener(
          "mouseover",
          () => {
            addHoverForNav(dropDown, el, "LEAF");
          },
          { passive: true }
        );

        dropDown.addEventListener(
          "mouseleave",
          () => {
            removeAllHoverForNav(el, "LEAF");
          },
          { passive: true }
        );

        dropDown.addEventListener("keydown", e => {
          if (ACTION_KEY_CODES.includes(e.keyCode)) {
            e.preventDefault();
            addHoverForNav(dropDown, el, "LEAF");
            dropDown.nextElementSibling.querySelectorAll(".is--tabbable-hidden").forEach(tabbableLink => {
              tabbableLink.setAttribute("tabindex", "0");
            });
          }
        });
      });
    }
    if ("ontouchstart" in document.documentElement) {
      el.querySelectorAll("nav a#products.head").forEach(navLink => {
        navLink.addEventListener("touchstart", e => {
          e.preventDefault();
        });
      });
    }
    // Set the default feature image
    this.getAndSetFeatureImage(el.querySelector("ul .nav .mobilefly li a"));

    const divOverlay = document.createElement("div");
    divOverlay.id = "content--overlay";
    divOverlay.classList.add("overlay-dark--full");
    divOverlay.style.display = "none";
    divOverlay.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
    document.body.appendChild(divOverlay);

    document.querySelector("#content--overlay").addEventListener("touchstart", () => {
      removeAllHoverForNav(el, "LEAF");
    });
    this.importantMessageLightBox = listenForImportantMessage();
  }

  getClosest(elem, selector) {
    let newElem = elem;
    // Fallback for IE
    if (!Element.prototype.matches) {
      Element.prototype.matches = Element.prototype.msMatchesSelector;
    }
    for (; newElem && newElem !== document; newElem = newElem.parentNode) {
      if (newElem.matches(selector)) {
        return newElem;
      }
    }
    return null;
  }

  getAndSetFeatureImage(hoveredEl) {
    let featureImageBox, imageName, imageText;
    if (hoveredEl.parentNode.classList) {
      featureImageBox = this.getClosest(hoveredEl, ".container").querySelector(".feature .content");
    }
    featureImageBox.href = hoveredEl.getAttribute("href");
    imageName = hoveredEl.parentNode.classList
      .toString()
      .replace("sectionhead", "")
      .replace("style-scope", "")
      .replace("arcteryx-leaf-header", "")
      .trim();
    imageText = hoveredEl.textContent;
    const imageUrl = `https://images-dynamic-arcteryx.imgix.net/nav/leaf/s20/${imageName}.jpg?auto=format`;
    setTimeout(() => {
      featureImageBox.style.backgroundImage = `url(${imageUrl})`;
      featureImageBox.querySelector("h6").textContent = imageText;
    }, 300);
  }
}

export default Header;
